/**
 * Prefetch lazy route chunks so tab switches feel native-app instant.
 * Mobile has no hover, and requestIdleCallback is heavily throttled on iOS —
 * call scheduleShellPrefetch() right after the authenticated shell mounts.
 */
import { CAROUSEL_TAB_ORDER } from '@/lib/shell';

const loaders: Record<string, () => Promise<unknown>> = {
  '/': () => import('@/pages/Dashboard'),
  '/library': () => import('@/pages/Library'),
  '/report': () => import('@/pages/AnnualReport'),
  '/profile': () => import('@/pages/Profile'),
  '/account': () => import('@/pages/Account'),
  '/new': () => import('@/pages/NewSurgery'),
  '/settings': () => import('@/pages/Settings'),
  '/procedures': () => import('@/pages/Procedures'),
  // Heavy (visx) — hover/pointer prefetch only, never idle-warm.
  '/research': () => import('@/pages/Research'),
};

const warmed = new Set<string>();

/** Bottom-bar tabs — highest priority after login. */
export const MOBILE_TAB_ROUTES = [...CAROUSEL_TAB_ORDER] as const;

/**
 * Tabs safe to idle-warm after shell load. `/report` pulls recharts (~475 KB)
 * and must stay hover/pointer-only — auto-warming it dominated Lighthouse
 * unused-JS / TTI on signed-in navigations (Performance ~54, LCP 12 s).
 */
export const LIGHT_TAB_ROUTES = ['/', '/library', '/profile'] as const;

/** Secondary shell pages (sidebar / deep links). */
export const SECONDARY_SHELL_ROUTES = [
  '/procedures',
  '/settings',
  '/account',
] as const;

function prefersSaveData(): boolean {
  if (typeof navigator === 'undefined') return false;
  const conn = (navigator as Navigator & {
    connection?: { saveData?: boolean };
  }).connection;
  return Boolean(conn?.saveData);
}

function prefersSlowConnection(): boolean {
  if (typeof navigator === 'undefined') return false;
  const conn = (navigator as Navigator & {
    connection?: { effectiveType?: string };
  }).connection;
  return conn?.effectiveType === '2g' || conn?.effectiveType === 'slow-2g';
}

/** Fire-and-forget chunk prefetch for a known app path. */
export function prefetchRoute(path: string): void {
  const base = path.split('?')[0]?.split('#')[0] ?? path;
  const key = base.startsWith('/edit/')
    ? '/new'
    : base.startsWith('/surgery/')
      ? '/surgery'
      : base;
  if (warmed.has(key)) return;
  const load =
    loaders[key] ??
    (key === '/surgery' ? () => import('@/pages/SurgeryDetail') : undefined);
  if (!load) return;
  warmed.add(key);
  void load().catch(() => {
    warmed.delete(key);
  });
}

/** Warm the light mobile tabs (Home / Library / Profile) — not Report. */
export function prefetchMobileTabs(): void {
  for (const path of LIGHT_TAB_ROUTES) prefetchRoute(path);
}

/** Warm sidebar / secondary routes. */
export function prefetchSecondaryShell(): void {
  for (const path of SECONDARY_SHELL_ROUTES) prefetchRoute(path);
}

/**
 * After the shell loads: warm light tabs when the browser goes idle, then
 * secondary routes. Skips warming on Save-Data / slow connections.
 * `/new` and `/report` stay hover/pointer-prefetch only (Navbar) — auto-warming
 * them pulled hundreds of KB into the critical-load window.
 * Returns a cleanup that clears pending timers.
 */
export function scheduleShellPrefetch(): () => void {
  if (typeof globalThis.setTimeout !== 'function') return () => {};

  const timers: number[] = [];
  const schedule = (fn: () => void, ms: number) => {
    timers.push(globalThis.setTimeout(fn, ms) as unknown as number);
  };

  const warm = () => {
    prefetchMobileTabs();
    if (!prefersSaveData() && !prefersSlowConnection()) {
      schedule(prefetchSecondaryShell, 400);
    }
  };

  // Wait for window load + idle so warming never competes with FCP/LCP —
  // firing right after first paint pushed ~1 MB of route chunks into the
  // critical window (Lighthouse: FCP 8.3 s on /account).
  const start = () => {
    const ric =
      typeof window !== 'undefined'
        ? window.requestIdleCallback?.bind(window)
        : undefined;
    if (ric) {
      ric(warm, { timeout: 5000 });
    } else {
      schedule(warm, 2500);
    }
  };

  if (typeof document !== 'undefined' && document.readyState === 'complete') {
    start();
  } else if (typeof window !== 'undefined') {
    window.addEventListener('load', start, { once: true });
  } else {
    start();
  }

  const onVisible = () => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return;
    }
    prefetchMobileTabs();
  };
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisible);
  }

  return () => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('load', start);
    }
    for (const id of timers) globalThis.clearTimeout(id);
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisible);
    }
  };
}
